import type { SVGProps } from 'react';
import { forwardRef } from 'react';
import { cn } from '../lib/cn';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'ref'> {
  /** Edge length in px. Icons are drawn on a 24×24 grid and scale cleanly. */
  size?: number;
  /**
   * Decorative by default. Pass a `title` to expose the icon to assistive tech;
   * otherwise it is hidden with aria-hidden so screen readers don't announce
   * every HUD glyph.
   */
  title?: string;
}

/**
 * Base SVG shell for every icon in the set.
 *
 * `currentColor` throughout — an icon takes its colour from the surrounding
 * text class (`text-titan`, `text-danger`, …), which is what makes the resource
 * accent system work without per-icon colour props.
 */
export const Icon = forwardRef<SVGSVGElement, IconProps>(function Icon(
  { size = 20, title, className, children, ...rest },
  ref,
) {
  return (
    <svg
      ref={ref}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      focusable="false"
      className={cn('shrink-0', className)}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
});
